import { AnimatePresence } from 'motion/react';
import React, { memo } from 'react';
import OpacityMotionTransition from '../../../components/OpacityMotionTransition';
import type { SelectTypes } from '../controls/RowColumnControl';
import RowColumnControl from '../controls/RowColumnControl';
import RowColumnInsertButton from '../controls/RowColumnInsertButton';
import RowColumnRearrangeDropIndicator from '../controls/RowColumnRearrangeDropIndicator';
import TableControl from '../controls/TableControl';
import type { RearrangingState } from '../types';
import type { Selection } from '../useTableSelection';
import { SelectionType, isRowOrColumnSelected } from '../useTableSelection';
import * as styles from './TableCellControl.module.scss';

interface TableCellControlProps {
  selection: Selection | null;
  rowIndex: number;
  columnIndex: number;
  lastRow: boolean;
  lastColumn: boolean;
  rearrangingState: RearrangingState | null;
  onTableSelect: () => void;
  onRowOrColumnInsert: (index: number, isRow: boolean) => void;
  onRowOrColumnSelect: (
    index: number,
    options: { isRow: boolean; selectType: SelectTypes },
  ) => void;
  onRearrangingStateChange: (state: RearrangingState | null) => void;
  onIndexShift: (
    index: number,
    options: { isRow: boolean; insert: true },
  ) => void;
  onClickBackground: (x: number, y: number) => void;
  onRearrange: (from: number, to: number, isRow: boolean) => void;
}

const TableCellControl = memo(
  ({
    selection,
    rowIndex,
    columnIndex,
    lastRow,
    lastColumn,
    rearrangingState,
    onTableSelect,
    onRowOrColumnInsert,
    onRowOrColumnSelect,
    onIndexShift,
    onClickBackground,
    onRearrangingStateChange,
    onRearrange,
  }: TableCellControlProps) => {
    const handleInsert = ({
      targetIndex,
      direction,
    }: {
      targetIndex: number;
      direction: 'row' | 'column';
    }) => {
      onRowOrColumnInsert(targetIndex, direction === 'row');
      onIndexShift(targetIndex, { isRow: direction === 'row', insert: true });
    };

    return (
      <>
        {rowIndex === 0 && (
          <div
            className={styles.captureBackgroundClick}
            onClick={(e) => onClickBackground(e.clientX, e.clientY)}
          />
        )}
        {columnIndex === 0 && (
          <>
            <RowColumnControl
              direction="row"
              index={rowIndex}
              last={lastRow}
              rearrangingState={rearrangingState}
              getIsSelected={(index) =>
                isRowOrColumnSelected({
                  index,
                  selection,
                  isRow: true,
                })
              }
              onSelect={onRowOrColumnSelect}
              onRearrangingStateChange={onRearrangingStateChange}
              onRearrange={onRearrange}
            />
            {rearrangingState?.to === rowIndex && rearrangingState.isRow && (
              <RowColumnRearrangeDropIndicator
                direction="row"
                target={
                  rearrangingState.from < rearrangingState.to
                    ? 'trailing'
                    : 'leading'
                }
              />
            )}
            {rowIndex === 0 && (
              <RowColumnInsertButton
                first
                direction="row"
                rearranging={false}
                onInsert={() =>
                  handleInsert({ targetIndex: rowIndex, direction: 'row' })
                }
              />
            )}
            <RowColumnInsertButton
              direction="row"
              rearranging={false}
              onInsert={() =>
                handleInsert({
                  targetIndex: rowIndex + 1,
                  direction: 'row',
                })
              }
            />
          </>
        )}
        {rowIndex === 0 && (
          <>
            <RowColumnControl
              direction="column"
              index={columnIndex}
              last={lastColumn}
              rearrangingState={rearrangingState}
              getIsSelected={(index) =>
                isRowOrColumnSelected({
                  index,
                  selection,
                  isRow: false,
                })
              }
              onSelect={onRowOrColumnSelect}
              onRearrangingStateChange={onRearrangingStateChange}
              onRearrange={onRearrange}
            />
            <AnimatePresence>
              {rearrangingState?.to === columnIndex &&
                !rearrangingState.isRow && (
                  <OpacityMotionTransition>
                    <RowColumnRearrangeDropIndicator
                      direction="column"
                      target={
                        rearrangingState.from < rearrangingState.to
                          ? 'trailing'
                          : 'leading'
                      }
                    />
                  </OpacityMotionTransition>
                )}
            </AnimatePresence>

            {columnIndex === 0 && (
              <RowColumnInsertButton
                first
                direction="column"
                rearranging={false}
                onInsert={() =>
                  handleInsert({
                    targetIndex: columnIndex,
                    direction: 'column',
                  })
                }
              />
            )}
            <RowColumnInsertButton
              direction="column"
              rearranging={false}
              onInsert={() =>
                handleInsert({
                  targetIndex: columnIndex + 1,
                  direction: 'column',
                })
              }
            />
          </>
        )}
        {rowIndex === 0 && columnIndex === 0 && (
          <TableControl
            selected={
              selection ? selection.type === SelectionType.Table : false
            }
            onSelect={onTableSelect}
          />
        )}
      </>
    );
  },
);

export default TableCellControl;
